import React, { useState } from 'react';
import { 
  FolderOpen, 
  FileText, 
  BookOpen, 
  Database, 
  ChevronDown, 
  ChevronUp,
  Settings,
  X,
  Plus,
  Sparkles
} from 'lucide-react';
import { useGlobalContext } from '../../contexts/GlobalContextProvider';

const GlobalContextBarApple = ({ onOpenModal, isDarkMode = false }) => {
  const {
    selectedProject,
    selectedInstruction,
    selectedGlossary,
    selectedTM,
    hasContext,
    clearContext,
    setShowContextModal,
  } = useGlobalContext();

  const [isExpanded, setIsExpanded] = useState(false);

  const handleOpenModal = () => {
    if (onOpenModal) {
      onOpenModal();
    } else {
      setShowContextModal(true);
    }
  };

  // Apple design tokens
  const colors = {
    background: isDarkMode ? 'rgba(28, 28, 30, 0.8)' : 'rgba(255, 255, 255, 0.8)',
    backgroundHover: isDarkMode ? 'rgba(44, 44, 46, 0.9)' : 'rgba(255, 255, 255, 0.95)',
    border: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
    borderDashed: isDarkMode ? 'rgba(147, 51, 234, 0.4)' : 'rgba(147, 51, 234, 0.3)',
    text: isDarkMode ? '#F5F5F7' : '#1D1D1F',
    textSecondary: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#86868B',
    textMuted: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : '#6B7280',
    cardBg: isDarkMode ? 'rgba(44, 44, 46, 0.6)' : 'rgba(0, 0, 0, 0.02)',
    pillBg: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
  };

  // Empty state - no context selected
  if (!hasContext) {
    return (
      <div 
        className="global-context-bar-apple global-context-bar-apple--empty"
        style={{
          background: isDarkMode 
            ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)'
            : 'linear-gradient(135deg, rgba(147, 51, 234, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '20px 24px',
          marginBottom: '24px',
          border: `1px dashed ${colors.borderDashed}`,
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onClick={handleOpenModal}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = isDarkMode 
            ? '0 8px 32px rgba(147, 51, 234, 0.2)'
            : '0 8px 32px rgba(147, 51, 234, 0.15)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        data-testid="apple-context-bar-empty"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #9333EA 0%, #3B82F6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
            }}>
              <Sparkles size={24} color="white" />
            </div>
            <div>
              <h4 style={{ 
                margin: 0, 
                fontSize: '17px', 
                fontWeight: '600', 
                color: colors.text,
                letterSpacing: '-0.4px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              }}>
                Set Translation Context
              </h4>
              <p style={{ 
                margin: '4px 0 0', 
                fontSize: '14px', 
                color: colors.textSecondary,
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
              }}>
                Apply project settings, glossary, TM & instructions
              </p>
            </div>
          </div>
          <div style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #9333EA 0%, #3B82F6 100%)',
            borderRadius: '12px',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
            boxShadow: '0 2px 8px rgba(147, 51, 234, 0.3)',
          }}>
            Configure
          </div>
        </div>
      </div>
    );
  }

  // Collapsed state - minimal display
  if (!isExpanded) {
    return (
      <div 
        className="global-context-bar-apple global-context-bar-apple--collapsed"
        style={{
          background: colors.background,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '16px',
          padding: '12px 18px',
          marginBottom: '20px',
          border: `1px solid ${colors.border}`,
          boxShadow: isDarkMode ? '0 2px 12px rgba(0, 0, 0, 0.3)' : '0 2px 12px rgba(0, 0, 0, 0.06)',
        }}
        data-testid="apple-context-bar-collapsed"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, overflow: 'hidden' }}>
            {/* Context Pills */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', overflow: 'hidden' }}>
              {selectedProject && (
                <ContextPill 
                  icon={FolderOpen} 
                  label={selectedProject.name} 
                  color="#9333EA" 
                  isDarkMode={isDarkMode}
                />
              )}
              {selectedInstruction && (
                <ContextPill 
                  icon={FileText} 
                  label={selectedInstruction.name} 
                  color="#3B82F6" 
                  isDarkMode={isDarkMode}
                />
              )}
              {selectedGlossary && (
                <ContextPill 
                  icon={BookOpen} 
                  label={selectedGlossary.name} 
                  color="#10B981" 
                  isDarkMode={isDarkMode}
                />
              )}
              {selectedTM && (
                <ContextPill 
                  icon={Database} 
                  label={selectedTM.name} 
                  color="#F59E0B" 
                  isDarkMode={isDarkMode}
                />
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '12px' }}>
            <IconButton 
              icon={ChevronDown} 
              onClick={() => setIsExpanded(true)} 
              isDarkMode={isDarkMode}
              title="Expand"
            />
            <IconButton 
              icon={Settings} 
              onClick={handleOpenModal} 
              isDarkMode={isDarkMode}
              title="Edit context"
            />
          </div>
        </div>
      </div>
    );
  }

  // Expanded state - full details
  return (
    <div 
      className="global-context-bar-apple global-context-bar-apple--expanded"
      style={{
        background: colors.background,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '24px',
        marginBottom: '24px',
        border: `1px solid ${colors.border}`,
        boxShadow: isDarkMode ? '0 4px 24px rgba(0, 0, 0, 0.4)' : '0 4px 24px rgba(0, 0, 0, 0.08)',
      }}
      data-testid="apple-context-bar-expanded"
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h4 style={{ 
          margin: 0, 
          fontSize: '15px', 
          fontWeight: '600', 
          color: colors.text,
          letterSpacing: '-0.3px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
        }}>
          Active Translation Context
        </h4>
        <IconButton 
          icon={ChevronUp} 
          onClick={() => setIsExpanded(false)} 
          isDarkMode={isDarkMode}
          title="Collapse"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
        <ContextCard 
          icon={FolderOpen} 
          label="Project" 
          value={selectedProject?.name} 
          color="#9333EA" 
          isDarkMode={isDarkMode}
        />
        <ContextCard 
          icon={FileText} 
          label="Instructions" 
          value={selectedInstruction?.name} 
          color="#3B82F6" 
          isDarkMode={isDarkMode}
        />
        <ContextCard 
          icon={BookOpen} 
          label="Glossary" 
          value={selectedGlossary?.name} 
          color="#10B981" 
          isDarkMode={isDarkMode}
        />
        <ContextCard 
          icon={Database} 
          label="Translation Memory" 
          value={selectedTM?.name} 
          color="#F59E0B" 
          isDarkMode={isDarkMode}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
        <button
          onClick={clearContext}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 18px',
            background: 'transparent',
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            cursor: 'pointer',
            color: colors.textSecondary,
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)';
            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
            e.currentTarget.style.color = '#DC2626';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = colors.border;
            e.currentTarget.style.color = colors.textSecondary;
          }}
        >
          <X size={14} />
          Clear All
        </button>
        <button
          onClick={handleOpenModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #9333EA 0%, #3B82F6 100%)',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(147, 51, 234, 0.3)',
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <Settings size={14} />
          Edit Context
        </button>
      </div>
    </div>
  );
};

// Sub-components
const ContextPill = ({ icon: Icon, label, color, isDarkMode }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: isDarkMode ? `${color}25` : `${color}15`,
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    color: isDarkMode ? color : color,
    whiteSpace: 'nowrap',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
  }}>
    <Icon size={14} />
    {label?.length > 14 ? label.slice(0, 14) + '...' : label}
  </div>
);

const ContextCard = ({ icon: Icon, label, value, color, isDarkMode }) => (
  <div style={{
    padding: '16px 18px',
    background: value 
      ? (isDarkMode ? `${color}15` : `linear-gradient(135deg, ${color}08 0%, ${color}04 100%)`)
      : (isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'),
    borderRadius: '16px',
    border: value 
      ? `1px solid ${isDarkMode ? `${color}30` : `${color}20`}` 
      : `1px dashed ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '12px',
        background: value ? color : (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Icon size={18} color={value ? 'white' : (isDarkMode ? 'rgba(255, 255, 255, 0.4)' : '#9CA3AF')} />
      </div>
      <div>
        <p style={{ 
          margin: 0, 
          fontSize: '11px', 
          color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6B7280', 
          fontWeight: '500', 
          textTransform: 'uppercase', 
          letterSpacing: '0.5px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
        }}>
          {label}
        </p>
        <p style={{ 
          margin: '3px 0 0', 
          fontSize: '14px', 
          fontWeight: '600', 
          color: value ? (isDarkMode ? color : color) : (isDarkMode ? 'rgba(255, 255, 255, 0.3)' : '#9CA3AF'),
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
        }}>
          {value || 'Not selected'}
        </p>
      </div>
    </div>
  </div>
);

const IconButton = ({ icon: Icon, onClick, isDarkMode, title }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      padding: 0,
      background: 'transparent',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6B7280',
      transition: 'all 0.15s ease',
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.background = 'transparent';
    }}
  >
    <Icon size={18} />
  </button>
);

export default GlobalContextBarApple;
